import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Breadcrumbs from "@/components/Breadcrumbs";
import Footer from "@/components/Footer";

interface FAQItem {
  question: string;
  answer: string;
}

const allStates = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL",
  "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME",
  "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH",
  "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
  "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

const faqItems: FAQItem[] = [
  {
    question: "What exactly do you do?",
    answer:
      "We build AI-powered tools that transform every citizen into an effective government watchdog. Our mission is to democratize access to public records by eliminating barriers that historically restricted transparency work to credentialed professionals and well-funded organizations. We provide free, open-source tools trained on thousands of FOIA requests to help you get the records you're entitled to.",
  },
  {
    question: "Why do you do this?",
    answer:
      "Elections are just the auditions—records are the receipts. Democracy requires informed citizens, and citizens need information to make informed decisions. Your government works for you. The law guarantees your right to see what it's doing. Transparency isn't optional—it is the rule. We exist because no citizen should need a lawyer or credentials to hold their government accountable.",
  },
  {
    question: "Who are you?",
    answer:
      "The HOLE Foundation is a 501(c)(3) private foundation dedicated to Honest Oversight & Legal Ethics. We were founded by Dr. Joseph T. Herrmann, M.D., specifically to address the growing crisis of government secrecy. Our team combines expertise in law, technology, and government accountability to build the infrastructure of transparency.",
  },
  {
    question: "Why is this so important?",
    answer:
      "Because you deserve to know what your government is doing. Today. Right now. It's the law. Records custodians are not employed to give you access to their records—they're employed by you to give you access to your records. And don't you forget that. Every citizen, regardless of credentials or funding, should have the same access to government records. That's democracy.",
  },
  {
    question: "What rights do I have?",
    answer:
      "You have affirmative rights to access public records under federal and state FOIA laws. These laws are not permissions—they are your rights as a citizen. Each state has its own FOIA statute, and the federal government has the Freedom of Information Act (FOIA). These laws require agencies to respond within specific timeframes and only allow limited exemptions for truly sensitive information.",
  },
  {
    question: "What are exemptions?",
    answer:
      "Exemptions are the excuses agencies are allowed to use to withhold information. Common exemptions include national security, privacy, law enforcement investigations, and privileged communications. However, exemptions are narrowly construed—agencies must prove why records qualify. Many exemptions are conditional, meaning agencies must still release information that doesn't harm the protected interest.",
  },
  {
    question: "How long does a FOIA request take?",
    answer:
      "Response times vary by jurisdiction. Federal agencies have 20 business days. State agencies typically have 3-15 business days, depending on the state. Some agencies provide expedited responses for urgent requests. If they miss the deadline, you may have appeal rights. Our Transparency Map shows response times for each state's specific statute.",
  },
  {
    question: "What can I do if they refuse to release records?",
    answer:
      "Most FOIA laws provide appeal processes. You can request an internal administrative appeal, and if that fails, you may have judicial enforcement rights. Some states allow citizens to sue for wrongful withholding. The key is following the proper procedures and meeting appeal deadlines, which vary by jurisdiction.",
  },
  {
    question: "How do I write a good FOIA request?",
    answer:
      "A good FOIA request is specific, reasonable, and narrowly tailored. Avoid vague requests that require excessive searching. Describe the records you want using specific terms like dates, names, or document types. Our FOIA Generator AI is trained on thousands of successful requests and helps you optimize your request for your specific jurisdiction.",
  },
];

interface DataSection {
  icon: string;
  title: string;
  description: string;
  items: string[];
}

const dataSections: DataSection[] = [
  {
    icon: "⏱️",
    title: "Response Times",
    description: "How long agencies must respond to your request",
    items: [
      "Federal: 20 business days",
      "State varies: 3-15 business days",
      "Expedited processing available",
      "Extension rules by jurisdiction",
    ],
  },
  {
    icon: "⚖️",
    title: "Exemptions",
    description: "Limited reasons agencies can withhold records",
    items: [
      "National security & defense",
      "Privacy & personal information",
      "Law enforcement investigations",
      "Business confidential information",
      "Privileged communications",
    ],
  },
  {
    icon: "📋",
    title: "Rights of Access",
    description: "Affirmative rights granted by law",
    items: [
      "Right to request records",
      "Right to explanation for denial",
      "Right to appeal",
      "Right to seek judicial review",
      "Right to attorney's fees (in some cases)",
    ],
  },
  {
    icon: "📞",
    title: "Appeals",
    description: "What to do if your request is denied",
    items: [
      "Administrative appeals process",
      "Appeal deadlines (vary by state)",
      "Judicial enforcement available",
      "Judicial review of denials",
      "Attorney's fees available",
    ],
  },
];

export default function Wiki() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [hoveredState, setHoveredState] = useState<string | null>(null);
  const navigate = useNavigate();

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const handleStateClick = (stateCode: string) => {
    navigate(`/wiki/${stateCode.toLowerCase()}`);
  };

  const handleFederalClick = () => {
    navigate("/wiki/federal/foia");
  };

  return (
    <div className="min-h-screen">
      <Navigation />

      <div className="w-full bg-brand-martinique">
        <div className="max-w-[1440px] mx-auto px-8 pt-4">
          <Breadcrumbs
            items={[{ label: "Transparency Wiki" }]}
            variant="dark"
          />
        </div>

        <section className="w-full bg-brand-martinique py-12 lg:py-16">
          <div className="max-w-[1440px] mx-auto px-8">
            <div className="space-y-4 mb-12">
              <h1 className="font-agency text-h1 font-bold text-white">
                Transparency Wiki
              </h1>
              <p className="font-kepler text-text-lg-custom text-brand-gray max-w-2xl">
                Your complete guide to public records access. Learn about FOIA
                laws, exemptions, your rights, and how to request government
                records with confidence.
              </p>
            </div>

            <div className="bg-brand-dark/30 rounded-lg p-6 border border-brand-orange/20 mb-12">
              <p className="font-kepler text-text-md-custom text-brand-gray italic leading-relaxed">
                <span className="text-brand-orange font-bold">
                  "Elections are just the auditions—records are the receipts."
                </span>{" "}
                Do you know what your government is doing today? You should.
                It's the law. Transparency isn't optional—it is the rule.
              </p>
            </div>
          </div>
        </section>

        <section className="w-full bg-brand-martinique border-t border-black/10 py-12 lg:py-16">
          <div className="max-w-[1440px] mx-auto px-8">
            <h2 className="font-agency text-h2 font-bold text-white mb-8">
              <span className="text-brand-orange">00</span> Navigate to State
              Information
            </h2>

            <div className="bg-white border border-brand-stroke rounded-lg p-8 mb-8">
              <p className="font-kepler text-text-md-custom text-brand-dark mb-6 text-center">
                Select a jurisdiction to view its transparency laws
              </p>

              <div className="grid grid-cols-5 lg:grid-cols-10 gap-3 mb-6">
                {allStates.map((code) => (
                  <button
                    key={code}
                    onMouseEnter={() => setHoveredState(code)}
                    onMouseLeave={() => setHoveredState(null)}
                    onClick={() => handleStateClick(code)}
                    className={`px-2 py-2 rounded text-xs font-bold transition-all duration-200 ${
                      hoveredState === code
                        ? "bg-brand-azure text-white scale-105"
                        : "bg-gray-200 text-brand-dark hover:bg-gray-300"
                    }`}
                  >
                    {code}
                  </button>
                ))}
              </div>

              <button
                onClick={handleFederalClick}
                className="w-full px-4 py-3 bg-brand-dark rounded-lg text-white font-kepler font-bold hover:bg-brand-dark/90 transition-colors flex items-center justify-center gap-2"
              >
                <span>Federal FOIA Law (52nd Jurisdiction)</span>
              </button>
            </div>
          </div>
        </section>

        <section className="w-full bg-brand-martinique border-t border-black/10 py-12 lg:py-16">
          <div className="max-w-[1440px] mx-auto px-8">
            <h2 className="font-agency text-h2 font-bold text-white mb-12">
              <span className="text-brand-orange">01</span> Data Sections
            </h2>

            <div className="grid lg:grid-cols-2 gap-6">
              {dataSections.map((section, idx) => (
                <div
                  key={idx}
                  className="bg-brand-dark rounded-lg p-6 space-y-4 border border-brand-orange/20 hover:border-brand-orange/50 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <span className="text-3xl">{section.icon}</span>
                    <div className="flex-1">
                      <h3 className="font-agency text-h4 font-bold text-white">
                        {section.title}
                      </h3>
                      <p className="text-brand-orange font-kepler text-sm mt-1">
                        {section.description}
                      </p>
                    </div>
                  </div>
                  <ul className="space-y-2 pl-6 font-kepler text-sm text-brand-gray list-disc">
                    {section.items.map((item, itemIdx) => (
                      <li key={itemIdx}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-brand-martinique border-t border-black/10 py-12 lg:py-16">
          <div className="max-w-[1440px] mx-auto px-8">
            <h2 className="font-agency text-h2 font-bold text-white mb-3">
              <span className="text-brand-orange">02</span> Using AI for Public
              Records Access
            </h2>
            <p className="font-kepler text-text-lg-custom text-brand-gray mb-8 max-w-2xl">
              Artificial intelligence can help you navigate FOIA laws and
              optimize your requests.
            </p>

            <div className="grid lg:grid-cols-3 gap-6">
              <div className="bg-brand-dark rounded-lg p-6 space-y-4">
                <h3 className="font-agency text-h5 font-bold text-white">
                  Understanding Your Rights
                </h3>
                <p className="font-kepler text-sm text-brand-gray leading-relaxed">
                  AI can analyze your state's specific FOIA statute and explain
                  your rights in plain language, showing you exactly what
                  information you can request and what exemptions apply.
                </p>
              </div>

              <div className="bg-brand-dark rounded-lg p-6 space-y-4">
                <h3 className="font-agency text-h5 font-bold text-white">
                  Optimizing Requests
                </h3>
                <p className="font-kepler text-sm text-brand-gray leading-relaxed">
                  Our AI is trained on thousands of successful FOIA requests. It
                  helps you craft precise, narrowly-tailored requests that
                  agencies can't easily reject and increase your chances of
                  success.
                </p>
              </div>

              <div className="bg-brand-dark rounded-lg p-6 space-y-4">
                <h3 className="font-agency text-h5 font-bold text-white">
                  Predicting Exemptions
                </h3>
                <p className="font-kepler text-sm text-brand-gray leading-relaxed">
                  AI can identify likely exemptions and help you craft
                  counter-arguments. When agencies do withhold records, you'll
                  know exactly what to challenge in your appeal.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-brand-martinique border-t border-black/10 py-12 lg:py-16">
          <div className="max-w-[1440px] mx-auto px-8">
            <h2 className="font-agency text-h2 font-bold text-white mb-3">
              <span className="text-brand-orange">03</span> Frequently Asked
              Questions
            </h2>
            <p className="font-kepler text-text-lg-custom text-brand-gray mb-8 max-w-2xl">
              Get answers to common questions about public records access, your
              rights, and how to use our tools.
            </p>

            <div className="space-y-3">
              {faqItems.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-brand-dark rounded-lg border border-brand-orange/20 overflow-hidden"
                >
                  <button
                    onClick={() => toggleFAQ(idx)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-brand-dark/80 transition-colors"
                  >
                    <h3 className="font-kepler font-bold text-white text-left text-base">
                      {item.question}
                    </h3>
                    <svg
                      className={`w-5 h-5 text-brand-orange flex-shrink-0 transition-transform ${
                        openFAQ === idx ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 14l-7 7m0 0l-7-7m7 7V3"
                      />
                    </svg>
                  </button>

                  {openFAQ === idx && (
                    <div className="px-6 py-4 border-t border-brand-orange/20 bg-brand-dark/50">
                      <p className="font-kepler text-sm text-brand-gray leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-brand-martinique border-t border-black/10 py-12 lg:py-16">
          <div className="max-w-[1440px] mx-auto px-8">
            <h2 className="font-agency text-h2 font-bold text-white mb-3">
              <span className="text-brand-orange">04</span> Remember
            </h2>
            <div className="bg-brand-dark/30 rounded-lg p-8 border-l-4 border-brand-orange space-y-4">
              <p className="font-kepler text-text-lg-custom text-brand-gray">
                <span className="text-brand-orange font-bold">
                  Those records are already yours.
                </span>
              </p>
              <p className="font-kepler text-text-lg-custom text-brand-gray">
                Records custodians are not employed to give you access to their
                records—they're employed by
                <span className="text-brand-orange font-bold"> you </span>
                to give you access to
                <span className="text-brand-orange font-bold">
                  {" "}
                  your records.
                </span>
              </p>
              <p className="font-kepler text-text-lg-custom text-brand-gray">
                And don't you forget that.
              </p>
            </div>
          </div>
        </section>

        <section className="w-full bg-brand-martinique border-t border-black/10 py-12 lg:py-16">
          <div className="max-w-[1440px] mx-auto px-8">
            <h2 className="font-agency text-h2 font-bold text-white mb-8">
              <span className="text-brand-orange">05</span> Getting Started
            </h2>

            <div className="grid lg:grid-cols-3 gap-8">
              <div className="space-y-4">
                <div className="text-4xl text-brand-orange font-bold">1</div>
                <h3 className="font-agency text-h4 font-bold text-white">
                  Learn Your Rights
                </h3>
                <p className="font-kepler text-text-md-custom text-brand-gray">
                  Explore the Transparency Map to understand the FOIA laws
                  specific to your jurisdiction.
                </p>
                <a
                  href="/transparency-map"
                  className="inline-flex items-center gap-2 text-brand-azure hover:text-brand-orange transition-colors font-kepler font-bold"
                >
                  View Transparency Map →
                </a>
              </div>

              <div className="space-y-4">
                <div className="text-4xl text-brand-orange font-bold">2</div>
                <h3 className="font-agency text-h4 font-bold text-white">
                  Craft Your Request
                </h3>
                <p className="font-kepler text-text-md-custom text-brand-gray">
                  Use our AI-powered FOIA Generator to create an optimized
                  request for your specific jurisdiction.
                </p>
                <a
                  href="/foia-generator"
                  className="inline-flex items-center gap-2 text-brand-azure hover:text-brand-orange transition-colors font-kepler font-bold"
                >
                  Generate Request →
                </a>
              </div>

              <div className="space-y-4">
                <div className="text-4xl text-brand-orange font-bold">3</div>
                <h3 className="font-agency text-h4 font-bold text-white">
                  Submit & Follow Up
                </h3>
                <p className="font-kepler text-text-md-custom text-brand-gray">
                  Submit your request to the appropriate agency and know your
                  appeal rights if they deny access.
                </p>
                <a
                  href="/"
                  className="inline-flex items-center gap-2 text-brand-azure hover:text-brand-orange transition-colors font-kepler font-bold"
                >
                  Learn More →
                </a>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </div>
  );
}
